import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ResumeEditor from './components/ResumeEditor';
import SuggestionPanel from './components/SuggestionPanel';
import KeywordAnalysis from './components/KeywordAnalysis';
import VersionHistory from './components/VersionHistory';
import ExportOptions from './components/ExportOptions';

const ResumeRewriterTool = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('editor');
  const [resumeContent, setResumeContent] = useState('');
  const [matchScore, setMatchScore] = useState(72);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [targetRole, setTargetRole] = useState('Frontend Developer');

  // Mock data for suggestions
  const [suggestions] = useState([
    {
      id: 1,
      title: 'Add quantified achievements',
      description: 'Include specific numbers and metrics to demonstrate impact',
      section: 'Experience',
      priority: 'critical',
      originalText: 'Improved website performance',
      improvedText: 'Improved website performance by 40%, reducing page load time from 3.2s to 1.9s',
      keywords: ['performance optimization', 'metrics', 'page load time'],
      scoreImpact: 8
    },
    {
      id: 2,
      title: 'Include modern frontend technologies',
      description: 'Add current industry-standard technologies to match job requirements',
      section: 'Skills',
      priority: 'critical',
      originalText: 'JavaScript, HTML, CSS',
      improvedText: 'JavaScript (ES6+), TypeScript, React.js, Next.js, HTML5, CSS3, Tailwind CSS',
      keywords: ['TypeScript', 'React.js', 'Next.js', 'Tailwind CSS'],
      scoreImpact: 12
    },
    {
      id: 3,
      title: 'Enhance project descriptions',
      description: 'Provide more context about your role and technologies used',
      section: 'Projects',
      priority: 'recommended',
      originalText: 'Built an e-commerce website',
      improvedText: 'Developed a full-stack e-commerce platform using React.js and Node.js, implementing secure payment integration with Stripe API and achieving 99.9% uptime',
      keywords: ['full-stack', 'Node.js', 'Stripe API', 'uptime'],
      scoreImpact: 6
    },
    {
      id: 4,
      title: 'Add soft skills',
      description: 'Include relevant soft skills that employers value',
      section: 'Skills',
      priority: 'bonus',
      originalText: 'Technical Skills: React, Node.js',
      improvedText: 'Technical Skills: React, Node.js\nSoft Skills: Team Leadership, Agile Methodology, Problem Solving, Cross-functional Collaboration',
      keywords: ['team leadership', 'agile', 'collaboration'],
      scoreImpact: 4
    }
  ]);

  // Mock data for keywords
  const [keywords] = useState([
    {
      id: 1,
      term: 'React.js',
      category: 'technical',
      status: 'present',
      density: 85,
      importance: 5,
      context: 'Frontend framework essential for modern web development'
    },
    {
      id: 2,
      term: 'TypeScript',
      category: 'technical',
      status: 'missing',
      density: 0,
      importance: 4,
      context: 'Strongly typed JavaScript for better code quality'
    },
    {
      id: 3,
      term: 'Node.js',
      category: 'technical',
      status: 'partial',
      density: 45,
      importance: 4,
      context: 'Backend JavaScript runtime for full-stack development'
    },
    {
      id: 4,
      term: 'Team Leadership',
      category: 'soft',
      status: 'missing',
      density: 0,
      importance: 3,
      context: 'Leading and mentoring development teams'
    },
    {
      id: 5,
      term: 'Docker',
      category: 'tools',
      status: 'missing',
      density: 0,
      importance: 3,
      context: 'Containerization for deployment and development'
    }
  ]);

  // Mock data for version history
  const [versions] = useState([
    {
      id: 1,
      name: 'Original Resume',
      createdAt: new Date('2024-10-01T10:00:00'),
      changes: 0,
      matchScore: 58,
      isAutoSave: false,
      description: 'Initial resume upload',
      changesSummary: []
    },
    {
      id: 2,
      name: 'Added Quantified Achievements',
      createdAt: new Date('2024-10-02T14:30:00'),
      changes: 5,
      matchScore: 65,
      isAutoSave: false,
      description: 'Applied AI suggestions for quantified achievements',
      changesSummary: [
        { type: 'added', description: 'Added performance metrics to experience section' },
        { type: 'modified', description: 'Enhanced project descriptions with numbers' }
      ]
    },
    {
      id: 3,
      name: 'Auto-save',
      createdAt: new Date('2024-10-03T07:45:00'),
      changes: 2,
      matchScore: 72,
      isAutoSave: true,
      description: 'Automatic save during editing session',
      changesSummary: [
        { type: 'added', description: 'Added TypeScript to skills section' },
        { type: 'modified', description: 'Updated contact information' }
      ]
    }
  ]);

  const [currentVersion] = useState(versions?.[versions?.length - 1]);

  const tabs = [
    { id: 'editor', label: 'Editor', icon: 'FileEdit' },
    { id: 'suggestions', label: 'AI Suggestions', icon: 'Sparkles', badge: suggestions?.length },
    { id: 'keywords', label: 'Keywords', icon: 'Hash' },
    { id: 'versions', label: 'History', icon: 'History' },
    { id: 'export', label: 'Export', icon: 'Download' }
  ];

  // Mock initial resume content
  useEffect(() => {
    setResumeContent(`RAHUL SHARMA
Frontend Developer

Email: rahul.sharma@email.com
Phone: +91 9876543210
Location: Bangalore, India
LinkedIn: linkedin.com/in/rahulsharma

PROFESSIONAL SUMMARY
Passionate Frontend Developer with 2+ years of experience in building responsive web applications. Skilled in JavaScript, React, and modern web technologies.

EXPERIENCE

Frontend Developer | TechCorp Solutions | Jan 2022 - Present
• Developed user interfaces for web applications
• Improved website performance
• Collaborated with design team
• Maintained code quality

Junior Developer | StartupXYZ | Jun 2021 - Dec 2021
• Built responsive websites
• Fixed bugs and issues
• Worked with senior developers

EDUCATION
B.Tech in Computer Science | ABC University | 2021
CGPA: 8.2/10

SKILLS
• JavaScript, HTML, CSS
• React, Redux
• Git, VS Code

PROJECTS

E-commerce Website
Built an e-commerce website with shopping cart functionality

Portfolio Website
Created personal portfolio to showcase projects`);
  }, []);

  const handleContentChange = (newContent) => {
    setResumeContent(newContent);
    // Simulate match score update
    const wordCount = newContent?.split(' ')?.length;
    const newScore = Math.min(95, Math.max(45, Math.floor(wordCount / 10) + 45));
    setMatchScore(newScore);
  };

  const handleAcceptSuggestion = (suggestionId) => {
    console.log('Accepting suggestion:', suggestionId);
    // Implement suggestion acceptance logic
  };

  const handleRejectSuggestion = (suggestionId) => {
    console.log('Rejecting suggestion:', suggestionId);
    // Implement suggestion rejection logic
  };

  const handleAcceptAllSuggestions = () => {
    console.log('Accepting all suggestions');
    // Implement accept all logic
  };

  const handleOptimizeKeywords = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      setIsAnalyzing(false);
      console.log('Keywords optimized');
    }, 2000);
  };

  const handleRestoreVersion = (versionId) => {
    console.log('Restoring version:', versionId);
    // Implement version restore logic
  };

  const handleDeleteVersion = (versionId) => {
    console.log('Deleting version:', versionId);
    // Implement version deletion logic
  };

  const handleCompareVersions = (version1Id, version2Id) => {
    console.log('Comparing versions:', version1Id, version2Id);
    // Implement version comparison logic
  };

  const handleExport = (exportData) => {
    setIsExporting(true);
    setTimeout(() => {
      setIsExporting(false);
      console.log('Exporting resume:', exportData);
      // Implement export logic
    }, 3000);
  };

  const roleOptions = [
    'Frontend Developer',
    'Backend Developer',
    'Full Stack Developer',
    'AI/ML Engineer',
    'GenAI Developer'
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Sparkles" size={24} className="text-primary" />
                  <h1 className="text-2xl font-bold text-foreground">AI Resume Rewriter</h1>
                </div>
                <p className="text-muted-foreground">
                  Optimize your resume with AI-powered suggestions and real-time improvements
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Target Role Selector */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">Target Role:</span>
                  <select
                    value={targetRole}
                    onChange={(e) => setTargetRole(e?.target?.value)}
                    className="px-3 py-1.5 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {roleOptions?.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>

                {/* Match Score */}
                <div className="flex items-center space-x-2 px-4 py-2 bg-primary/5 border border-primary/20 rounded-lg">
                  <Icon name="TrendingUp" size={16} className="text-primary" />
                  <span className="text-sm text-muted-foreground">Match Score:</span>
                  <span className="text-lg font-bold text-primary">{matchScore}%</span>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex space-x-1 mt-6">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                  {tab?.badge && (
                    <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">
                      {tab?.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Panel */}
            <div className="lg:col-span-2">
              {activeTab === 'editor' && (
                <ResumeEditor
                  content={resumeContent}
                  onContentChange={handleContentChange}
                  suggestions={suggestions}
                  onAcceptSuggestion={handleAcceptSuggestion}
                  onRejectSuggestion={handleRejectSuggestion}
                  isLoading={isAnalyzing}
                />
              )}

              {activeTab === 'suggestions' && (
                <SuggestionPanel
                  suggestions={suggestions}
                  onAcceptSuggestion={handleAcceptSuggestion}
                  onRejectSuggestion={handleRejectSuggestion}
                  onAcceptAll={handleAcceptAllSuggestions}
                  matchScore={matchScore}
                  isLoading={isAnalyzing}
                />
              )}

              {activeTab === 'keywords' && (
                <KeywordAnalysis
                  keywords={keywords}
                  targetRole={targetRole}
                  onOptimizeKeywords={handleOptimizeKeywords}
                />
              )}

              {activeTab === 'versions' && (
                <VersionHistory
                  versions={versions}
                  currentVersion={currentVersion}
                  onRestoreVersion={handleRestoreVersion}
                  onDeleteVersion={handleDeleteVersion}
                  onCompareVersions={handleCompareVersions}
                />
              )}

              {activeTab === 'export' && (
                <ExportOptions
                  onExport={handleExport}
                  isExporting={isExporting}
                  resumeContent={resumeContent}
                  matchScore={matchScore}
                />
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="font-semibold text-card-foreground mb-4 flex items-center space-x-2">
                  <Icon name="BarChart3" size={18} className="text-primary" />
                  <span>Optimization Stats</span>
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Match Score</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-16 bg-muted rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${matchScore}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-primary">{matchScore}%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Keywords Present</span>
                    <span className="text-sm font-medium text-success">
                      {keywords?.filter(k => k?.status === 'present')?.length}/{keywords?.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">AI Suggestions</span>
                    <span className="text-sm font-medium text-warning">{suggestions?.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Word Count</span>
                    <span className="text-sm font-medium text-card-foreground">
                      {resumeContent?.split(' ')?.filter(word => word?.length > 0)?.length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="font-semibold text-card-foreground mb-4 flex items-center space-x-2">
                  <Icon name="Zap" size={18} className="text-primary" />
                  <span>Quick Actions</span>
                </h3>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Sparkles"
                    iconPosition="left"
                    onClick={() => setActiveTab('suggestions')}
                  >
                    View AI Suggestions
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Hash"
                    iconPosition="left"
                    onClick={() => setActiveTab('keywords')}
                  >
                    Analyze Keywords
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Download"
                    iconPosition="left"
                    onClick={() => setActiveTab('export')}
                  >
                    Export Resume
                  </Button>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="font-semibold text-card-foreground mb-4 flex items-center space-x-2">
                  <Icon name="Lightbulb" size={18} className="text-primary" />
                  <span>Optimization Tips</span>
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={14} className="text-success mt-0.5" />
                    <span className="text-muted-foreground">
                      Use action verbs to start bullet points
                    </span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={14} className="text-success mt-0.5" />
                    <span className="text-muted-foreground">
                      Include quantified achievements with numbers
                    </span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={14} className="text-success mt-0.5" />
                    <span className="text-muted-foreground">
                      Match keywords from job descriptions
                    </span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={14} className="text-success mt-0.5" />
                    <span className="text-muted-foreground">
                      Keep formatting simple for ATS compatibility
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeRewriterTool;